<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0">
      <!-- Brand logo-->
      <b-link class="brand-logo">
        <vuexy-logo />
      </b-link>
      <!-- /Brand logo-->

      <!-- Left Text-->
      <b-col
        lg="8"
        class="d-none d-lg-flex align-items-center p-5"
      >
        <div class="w-100 d-lg-flex align-items-center justify-content-center px-5">
          <b-img
            fluid
            :src="imgUrl"
            :alt="$t('pages.setPassword')"
          />
        </div>
      </b-col>
      <!-- /Left Text-->

      <!-- Set password-->
      <b-col
        lg="4"
        class="d-flex align-items-center auth-bg px-2 p-lg-5"
      >
        <b-col
          sm="8"
          md="6"
          lg="12"
          class="px-xl-2 mx-auto"
        >
          <template v-if="state === 'invalid'">
            <b-card-title
              class="mb-1 font-weight-bold"
              title-tag="h2"
            >
              {{ $t('pages.linkExpired') }}
            </b-card-title>
            <b-card-text class="mb-2">
              {{ $t('pages.thisLinkIsInvalidHasExpiredOrWasAlready') }}
            </b-card-text>
            <b-button
              variant="primary"
              block
              :to="{ name: 'page-forgot-password' }"
            >
              {{ $t('pages.requestANewLink') }}
            </b-button>
          </template>

          <template v-else-if="state === 'error'">
            <b-card-title
              class="mb-1 font-weight-bold"
              title-tag="h2"
            >
              {{ $t('pages.somethingWentWrong') }}
            </b-card-title>
            <b-card-text class="mb-2">
              {{ $t('pages.weCouldNotCheckYourLinkRightNowYour') }}
            </b-card-text>
            <b-button
              variant="primary"
              block
              :disabled="isVerifying"
              @click="verifyToken"
            >
              {{ $t('pages.tryAgain') }}
            </b-button>
          </template>

          <template v-else-if="state === 'form'">
            <b-card-title
              class="mb-1 font-weight-bold"
              title-tag="h2"
            >
              {{ isActivation ? $t('pages.activateYourAccount') : $t('pages.resetPassword') }}
            </b-card-title>
            <b-card-text class="mb-2">
              {{ $t(isActivation ? 'pages.activationInstructions' : 'pages.resetInstructions') }}
            </b-card-text>

            <validation-observer
              ref="setPasswordForm"
              #default="{ invalid }"
            >
              <b-form
                class="auth-reset-password-form mt-2"
                @submit.prevent="setPassword"
              >
                <b-form-group
                  :label="$t('pages.newPassword')"
                  label-for="set-password-new"
                >
                  <validation-provider
                    #default="{ errors }"
                    :name="$t('pages.password')"
                    vid="password"
                    rules="required|min:8|max:15"
                  >
                    <b-input-group
                      class="input-group-merge"
                      :class="errors.length > 0 ? 'is-invalid' : null"
                    >
                      <b-form-input
                        id="set-password-new"
                        v-model="password"
                        :type="passwordFieldType"
                        :state="errors.length > 0 ? false : null"
                        class="form-control-merge"
                        name="set-password-new"
                        autocomplete="new-password"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          class="cursor-pointer"
                          :icon="passwordToggleIcon"
                          @click="togglePasswordVisibility"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>

                <b-form-group
                  :label="$t('pages.confirmPassword')"
                  label-for="set-password-confirm"
                >
                  <validation-provider
                    #default="{ errors }"
                    :name="$t('pages.confirmPassword')"
                    vid="Confirm Password"
                    rules="required|confirmed:password"
                  >
                    <b-form-input
                      id="set-password-confirm"
                      v-model="passwordConfirmation"
                      :type="passwordFieldType"
                      :state="errors.length > 0 ? false : null"
                      name="set-password-confirm"
                      autocomplete="new-password"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>

                <b-button
                  type="submit"
                  variant="primary"
                  block
                  :disabled="invalid || isSubmitting"
                >
                  {{ isActivation ? $t('pages.activateAccount') : $t('pages.setNewPassword') }}
                </b-button>
              </b-form>
            </validation-observer>
          </template>

          <b-card-text class="text-center mt-2">
            <b-link :to="{ name: 'page-login' }">
              <feather-icon icon="ChevronLeftIcon" />
              {{ $t('pages.backToLogin') }}
            </b-link>
          </b-card-text>
        </b-col>
      </b-col>
      <!-- /Set password-->
    </b-row>
  </div>
</template>

<script>
import i18n from '@/libs/i18n'

/* eslint-disable global-require */
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import VuexyLogo from '@core/layouts/components/Logo.vue'
import {
  BButton,
  BCardText,
  BCardTitle,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BImg,
  BInputGroup,
  BInputGroupAppend,
  BLink,
  BRow,
} from 'bootstrap-vue'
import {
  confirmed, max, min, required,
} from '@validations'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import store from '@/store/index'

export default {
  components: {
    BRow,
    BCol,
    BLink,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BCardText,
    BCardTitle,
    BImg,
    BForm,
    BButton,
    VuexyLogo,
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      token: '',
      state: 'verifying',
      purpose: '',
      password: '',
      passwordConfirmation: '',
      isVerifying: false,
      isSubmitting: false,
      sideImg: require('@/assets/images/pages/reset-password-v2.svg'),

      // validation rules
      required,
      min,
      max,
      confirmed,
    }
  },
  computed: {
    isActivation() {
      return this.purpose === 'activation'
    },
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    imgUrl() {
      if (store.state.appConfig.layout.skin === 'dark') {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.sideImg = require('@/assets/images/pages/reset-password-v2-dark.svg')
        return this.sideImg
      }
      return this.sideImg
    },
  },
  created() {
    this.init()
  },
  mounted() {
    window.addEventListener('hashchange', this.onHashChange)
  },
  beforeDestroy() {
    window.removeEventListener('hashchange', this.onHashChange)
  },
  methods: {
    onHashChange() {
      if (window.location.hash.includes('token=')) {
        this.init()
      }
    },
    init() {
      this.state = 'verifying'
      this.password = ''
      this.passwordConfirmation = ''
      this.token = this.takeTokenFromUrl()

      if (!this.token) {
        this.state = 'invalid'
        this.hideLoading()
        return
      }

      this.verifyToken()
    },
    takeTokenFromUrl() {
      const token = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('token') || ''

      if (window.location.hash) {
        window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`)
      }

      return token
    },
    verifyToken() {
      this.isVerifying = true
      this.showLoading()
      this.$http
        .post('/api/auth/password-token/verify', { token: this.token })
        .then(response => {
          this.purpose = response.data.purpose
          this.state = 'form'
        })
        .catch(errors => {
          if (errors.response?.status === 422) {
            this.forgetToken()
            return
          }
          this.alertApiError(errors, i18n.t('pages.couldNotCheckTheLink'))
          this.state = 'error'
        })
        .finally(() => {
          this.isVerifying = false
          this.hideLoading()
        })
    },
    setPassword() {
      this.$refs.setPasswordForm.validate().then(success => {
        if (!success) {
          return
        }

        this.isSubmitting = true
        this.showLoading()
        this.$http
          .post('/api/auth/set-password', {
            token: this.token,
            password: this.password,
            password_confirmation: this.passwordConfirmation,
          })
          .then(response => {
            this.token = ''
            this.alertSuccess(response.data.message)
            this.$router.push({ name: 'page-login' })
          })
          .catch(errors => {
            if (Array.isArray(errors.response?.data?.errors)) {
              this.forgetToken()
              return
            }
            this.alertApiError(errors, i18n.t('pages.couldNotSetThePasswordPleaseTryAgain'))
          })
          .finally(() => {
            this.isSubmitting = false
            this.hideLoading()
          })
      })
    },
    forgetToken() {
      this.token = ''
      this.state = 'invalid'
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/pages/page-auth.scss";
</style>
